/**
 * ModeSwitchButton Component
 * Simple button to switch between voice and text input modes
 * Shows Keyboard icon in voice mode, Mic icon in text mode
 */

import { Keyboard, Mic } from 'lucide-react';
import type { Language } from './VoiceOrderingContainer';

interface ModeSwitchButtonProps {
  mode: 'voice' | 'text';
  onModeChange: (mode: 'voice' | 'text') => void;
  language: Language;
  disabled?: boolean;
}

export function ModeSwitchButton({
  mode,
  onModeChange,
  language,
  disabled = false,
}: ModeSwitchButtonProps) {
  const isVoiceMode = mode === 'voice';
  const targetMode = isVoiceMode ? 'text' : 'voice';
  
  const ariaLabel = isVoiceMode
    ? language === 'en'
      ? 'Switch to text input'
      : 'التبديل إلى إدخال النص'
    : language === 'en'
    ? 'Switch to voice input'
    : 'التبديل إلى إدخال الصوت';

  const title = isVoiceMode
    ? language === 'en'
      ? 'Switch to text mode'
      : 'التبديل إلى وضع النص'
    : language === 'en'
    ? 'Switch to voice mode'
    : 'التبديل إلى وضع الصوت';

  return (
    <button
      onClick={() => !disabled && onModeChange(targetMode)}
      disabled={disabled}
      className="flex items-center justify-center rounded-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        width: '44px',
        height: '44px',
        minWidth: '44px',
        minHeight: '44px',
        background: disabled ? 'var(--border)' : 'var(--card-bg)',
        color: disabled ? 'var(--text-secondary)' : 'var(--text-primary)',
        border: `1.5px solid ${disabled ? 'var(--border)' : 'var(--border)'}`,
        boxShadow: disabled ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
      aria-label={ariaLabel}
      title={title}
    >
      {isVoiceMode ? (
        <Keyboard className="h-6 w-6" />
      ) : (
        <Mic className="h-6 w-6" />
      )}
    </button>
  );
}

